import { ChevronDownIcon } from 'lucide-react'
import { type ReactNode, useEffect, useId, useRef } from 'react'

import { cn } from '@/utils'
import { StepRail, type StepState } from './StepRail'

interface WizardStepProps {
  step: number
  title: string
  description?: string
  summary?: ReactNode
  open: boolean
  warning?: boolean
  isFirst?: boolean
  isLast?: boolean
  isCollapsible?: boolean
  onToggle: () => void
  children: ReactNode
}

export const WizardStep = ({
  step,
  title,
  description,
  summary,
  open,
  warning = false,
  isFirst = false,
  isLast = false,
  isCollapsible = true,
  onToggle,
  children,
}: WizardStepProps) => {
  const contentId = useId()
  const headerRef = useRef<HTMLButtonElement>(null)
  const wasOpen = useRef(open)

  // Closing hides whatever held focus, which would otherwise drop the user back on body.
  useEffect(() => {
    if (wasOpen.current && !open) headerRef.current?.focus()
    wasOpen.current = open
  }, [open])

  const state: StepState = open ? 'pending' : warning ? 'warning' : 'done'

  return (
    <div className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-x-3">
      <StepRail step={step} state={state} isFirst={isFirst} isLast={isLast} />

      <div className={cn('flex min-w-0 flex-col', !isLast && 'pb-6')}>
        <button
          ref={headerRef}
          type="button"
          onClick={onToggle}
          disabled={!isCollapsible}
          aria-expanded={open}
          aria-controls={contentId}
          className={cn(
            '-mx-2 flex items-center gap-3 rounded-md px-2 py-2.5 text-left',
            isCollapsible &&
              'cursor-pointer transition-colors hover:bg-blue-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          )}
        >
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="font-semibold leading-6 tracking-tight">
              {title}
            </span>
            {open || !summary
              ? description && (
                  <span className="text-sm text-muted-foreground">
                    {description}
                  </span>
                )
              : summary && (
                  <span
                    className={cn(
                      'truncate text-sm',
                      warning ? 'text-amber-700' : 'text-muted-foreground',
                    )}
                  >
                    {summary}
                  </span>
                )}
          </span>

          {isCollapsible && (
            <ChevronDownIcon
              className={cn(
                'size-4 shrink-0 text-muted-foreground transition-transform',
                !open && '-rotate-90',
              )}
            />
          )}
        </button>

        <div id={contentId} hidden={!open} className="mt-4">
          {children}
        </div>
      </div>
    </div>
  )
}
